import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle, Briefcase, Plane, Shield, X, Upload, Trash2 } from 'lucide-react';
import logger from '../../utils/logger';
import DynamicFormModal from '../../components/Site/DynamicFormModal';
import useExtraSolutions from './useExtraSolutions';

// Mapping ID solution → slug Form Builder (slugs normalisés)
const SOLUTION_SLUGS = {
  'annulation-voyage': 'annulation-de-voyage',
  'bagages-effets': 'bagages-effets-personnels',
  'assistance-voyage': 'assistance-rapatriement-voyage',
  'rc-voyage': 'responsabilite-civile-voyage'
};

const SolutionsVoyage = () => {
  const { t } = useTranslation('public');
  const [selectedFormSlug, setSelectedFormSlug] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Solutions pour voyage (4 cartes)
  const solutions = [
    {
      id: 'annulation-voyage',
      title: t('solutions.travel.cancellation.title'),
      description: t('solutions.travel.cancellation.description'),
      icon: XCircle,
      color: 'text-red-600'
    },
    {
      id: 'bagages-effets',
      title: t('solutions.travel.luggage.title'),
      description: t('solutions.travel.luggage.description'),
      icon: Briefcase,
      color: 'text-blue-600'
    },
    {
      id: 'assistance-voyage',
      title: t('solutions.travel.assistanceTravel.title'),
      description: t('solutions.travel.assistanceTravel.description'),
      icon: Plane,
      color: 'text-green-600'
    },
    {
      id: 'rc-voyage',
      title: t('solutions.travel.liability.title'),
      description: t('solutions.travel.liability.description'),
      icon: Shield,
      color: 'text-purple-600'
    }
  ];

  // Ouvrir le formulaire dynamique
  const openDevisModal = (slug) => {
    setSelectedFormSlug(slug || 'demande-generale');
  };

  const ICON_BY_SLUG = {};
  solutions.forEach(s => { const slug = SOLUTION_SLUGS[s.id]; if (slug) ICON_BY_SLUG[slug] = { icon: s.icon, color: s.color }; });
  const dbSolutions = useExtraSolutions('voyage', ICON_BY_SLUG);
  const allSolutions = dbSolutions.length ? dbSolutions : solutions;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-brand-primary to-brand-primary-hover text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            {t('solutions.travel.pageTitle')}
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">
            {t('solutions.travel.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {allSolutions.map((solution) => {
              const IconComponent = solution.icon;
              return (
                <div
                  key={solution.id}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full"
                >
                  <div className="p-8 flex flex-col flex-grow">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                      <div className="bg-gray-50 group-hover:bg-brand-primary/10 p-4 rounded-full transition-all duration-300">
                        <IconComponent 
                          size={48} 
                          className={`${solution.color} group-hover:text-brand-primary transition-colors duration-300`}
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-brand-text group-hover:text-brand-primary transition-colors duration-300 mb-4 text-center">
                      {solution.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-center mb-8 leading-relaxed flex-grow">
                      {solution.description}
                    </p>

                    {/* CTA Button */}
                    <div className="text-center mt-auto">
                      <button
                        onClick={() => openDevisModal(solution.slug || SOLUTION_SLUGS[solution.id])}
                        className="w-full bg-gray-100 text-brand-text px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary hover:text-white transition-all duration-300 hover:shadow-lg transform hover:scale-105"
                      >
                        {t('solutions.requestStudy')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-brand-primary to-brand-primary-hover py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {t('solutions.travel.ctaTitle')}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {t('solutions.travel.ctaSubtitle')}
          </p>
          <button
            onClick={() => setSelectedFormSlug('demande-generale')}
            className="bg-white text-brand-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 transform hover:-translate-y-1"
          >
            {t('solutions.freeStudyRequest')}
          </button>
        </div>
      </section>

      {/* Modal Devis */}

      {/* Modal Formulaire Dynamique */}
      {selectedFormSlug && (
        <DynamicFormModal
          slug={selectedFormSlug}
          onClose={() => setSelectedFormSlug(null)}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
};

export default SolutionsVoyage;
